import {
  Conversation,
  StartConversationParams,
  StartConversationResponse,
} from '@stripe-internal/conversations-client';

import {
  ConversationsApiCreateConversationEndpointRequest,
  ConversationsApiDataConversation,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ApiMethods, ErrorResponse, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {PageContextNormalizer} from './PageContextNormalizer';
import {isValidTriggerPoint} from './validators';

const DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS = 3;

type ValidatedStartConversationParams = StartConversationParams & {
  participant: NonNullable<StartConversationParams['participant']>;
  context: NonNullable<StartConversationParams['context']> & {
    audience: ConversationsApiCreateConversationEndpointRequest['audience'];
    entryPoint: ConversationsApiCreateConversationEndpointRequest['entry_point'];
  };
};

type SendMessageResult =
  | {type: 'success'; data: ConversationsApiDataConversation}
  | ErrorResponse;

type GetConversationResult = Awaited<ReturnType<ApiMethods['getConversation']>>;

type FrameworkConversation = ReturnType<
  typeof FrameworkAdapter.fromApiConversation
>;

export type StartConversationCreateProcessFailureMetricTags = {
  failure_step: 'delegate_first_message_to_server';
  reason: 'exception' | 'server_error' | 'recovered_after_duplicate_guard';
  attempt_count: number;
  error_code?: number;
};

type DelegateFirstMessageToServerFailureMetricTags = Omit<
  StartConversationCreateProcessFailureMetricTags,
  'failure_step'
>;

type RetrieveStartConversationParams = StartConversationParams & {
  conversationId: string;
  participant: NonNullable<StartConversationParams['participant']>;
};

type StartConversationHandlerDeps = {
  createConversation: (
    payload: ConversationsApiCreateConversationEndpointRequest,
  ) => ReturnType<ApiMethods['createConversation']>;
  sendMessage: (
    conversationId: string,
    payload: Parameters<ApiMethods['sendMessage']>[1],
  ) => Promise<SendMessageResult>;
  getConversation: (
    conversationId: string,
  ) => ReturnType<ApiMethods['getConversation']>;
  listConversations: (
    params: Parameters<ApiMethods['listConversations']>[0],
  ) => ReturnType<ApiMethods['listConversations']>;
  buildConversationInstance: ({
    frameworkConversation,
    params,
  }: {
    frameworkConversation: FrameworkConversation;
    params: ValidatedStartConversationParams | RetrieveStartConversationParams;
  }) => Conversation;
  emitDelegateFirstMessageToServerFailureMetric?: (
    tags: StartConversationCreateProcessFailureMetricTags,
  ) => void;
  enableUhPendingInput?: boolean;
  attachmentDownloadPath?: string;
  reportWarning?: ReportWarning;
};

export class StartConversationHandler {
  private static readonly AMBIENT_PARAMS_EXCLUDED_FROM_ADDITIONAL_PARAMS =
    new Set([
      'forestVersion',
      'link_testmode_merchant', // forwarded as a URL query param by wrapFetch instead
    ]);

  private deps: StartConversationHandlerDeps;

  constructor(deps: StartConversationHandlerDeps) {
    this.deps = deps;
  }

  call = async (
    params: StartConversationParams,
  ): Promise<StartConversationResponse> => {
    if (params.conversationId) {
      if (!params.participant) {
        return StartConversationHandler.error(400, 'Missing user');
      }
      const retrieveParams: RetrieveStartConversationParams = {
        ...params,
        conversationId: params.conversationId,
        participant: params.participant,
      };
      const response = await this.deps.getConversation(params.conversationId);
      if (!(response.type === 'error' && response.code === 404)) {
        return this.toRetrieveConversationResponse(response, retrieveParams);
      }
    }

    const validatedParams =
      StartConversationHandler.validateStartConversationParams(params);
    if (!validatedParams) {
      return StartConversationHandler.requiredParamError(params);
    }

    const startPayload =
      StartConversationHandler.buildStartPayload(validatedParams);

    const existingConversationId = params.skipInProgress
      ? null
      : await this.findExistingActiveConversation(startPayload);
    if (existingConversationId) {
      const retrieveParams: RetrieveStartConversationParams = {
        ...params,
        conversationId: existingConversationId,
        participant: validatedParams.participant,
      };
      const retrieveResponse = await this.deps.getConversation(
        existingConversationId,
      );
      if (
        !(retrieveResponse.type === 'error' && retrieveResponse.code === 404)
      ) {
        return this.toRetrieveConversationResponse(
          retrieveResponse,
          retrieveParams,
        );
      }
    }

    const response = await this.deps.createConversation(startPayload);

    if (response.type === 'error') {
      return this.toStartConversationResponse(response, validatedParams);
    }

    const sendMessageResponse =
      await this.delegateFirstMessageToServerWithRetries(
        response.data.id,
        validatedParams,
        startPayload.page_context,
      );

    return this.toStartConversationResponse(
      sendMessageResponse,
      validatedParams,
    );
  };

  private async delegateFirstMessageToServerWithRetries(
    conversationId: string,
    params: ValidatedStartConversationParams,
    pageContext: ConversationsApiCreateConversationEndpointRequest['page_context'],
    attempt = 1,
  ): Promise<SendMessageResult> {
    // Compute parts once so we can inspect them in the error-recovery path.
    const delegatedParts =
      StartConversationHandler.buildDelegatedFirstMessageParts(params);

    try {
      // NOTE: Unlike v1, create does not pass page_context into the initial
      // UserInput. We immediately send it on the follow-up send_message so the
      // first traversal still receives data_with_pii.pageContext.
      // When an initialQuery is supplied, preserve v1 parity by sending it as
      // the delegated first message instead of an empty server-go-first turn.
      const response = await this.deps.sendMessage(conversationId, {
        id: ApiMethods.generateMessageId(),
        participant_id: params.participant.id,
        participant_type: FrameworkAdapter.participantTypeFromRole(
          params.participant.role,
        ),
        parts: delegatedParts,
        page_context: pageContext,
      });

      // Recovery path: the backend's duplicate-guard fires a 400 with
      // "received empty message parts" when the conversation already has
      // messages (i.e. a prior attempt timed out at the gateway but the
      // backend finished the traversal and appended the greeting). This means
      // the server already completed the server-go-first turn — we can
      // recover by fetching the conversation that already has the greeting.
      //
      // Detection matches the guard's stable error_code
      // (EMPTY_MESSAGE_PARTS_CONVERSATION_STARTED in
      // send_message_endpoint_mixin.rb), with a message-substring fallback
      // that can be removed once the backend field is fully deployed.
      //
      // Apply recovery on any attempt number: a first-attempt 400 of this
      // kind (e.g. double-create race from two tabs) has the same answer.
      // Only triggered when parts are empty (no initialQuery/initialAttachment)
      // because non-empty parts cannot hit the backend guard.
      if (
        response.type === 'error' &&
        response.code === 400 &&
        delegatedParts.length === 0 &&
        StartConversationHandler.isEmptyPartsGuardError(response)
      ) {
        return this.recoverFromEmptyPartsGuard(
          conversationId,
          response,
          attempt,
        );
      }

      if (
        response.type !== 'error' ||
        !StartConversationHandler.shouldRetryApiError(response)
      ) {
        return response;
      }

      if (attempt === DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS) {
        this.emitDelegateFirstMessageToServerFailureMetric({
          reason: 'server_error',
          attempt_count: attempt,
          error_code: response.code,
        });
        return response;
      }
    } catch (e) {
      if (attempt === DELEGATE_FIRST_MESSAGE_TO_SERVER_MAX_ATTEMPTS) {
        this.emitDelegateFirstMessageToServerFailureMetric({
          reason: 'exception',
          attempt_count: attempt,
        });
        throw e;
      }
    }

    return this.delegateFirstMessageToServerWithRetries(
      conversationId,
      params,
      pageContext,
      attempt + 1,
    );
  }

  /**
   * Recovery handler for the empty-parts duplicate guard.
   *
   * When a prior send-message attempt timed out at the gateway but the backend
   * completed the traversal, subsequent attempts see the 400 guard. The
   * conversation is in a good state — we just need to fetch it.
   *
   * Emits a recovery metric so we can track how often the race fires in
   * QA/prod (reason: 'recovered_after_duplicate_guard'). If the fetch itself
   * fails, falls back to returning the original 400 error.
   */
  private async recoverFromEmptyPartsGuard(
    conversationId: string,
    originalError: ErrorResponse,
    attempt: number,
  ): Promise<SendMessageResult> {
    const getResponse = await this.deps.getConversation(conversationId);

    if (getResponse.type === 'error') {
      // Recovery fetch failed; return the original 400 rather than masking it.
      return originalError;
    }

    // Emit a distinguishable metric so we can track the race in QA/prod.
    this.emitDelegateFirstMessageToServerFailureMetric({
      reason: 'recovered_after_duplicate_guard',
      attempt_count: attempt,
      error_code: 400,
    });

    return {type: 'success', data: getResponse.data};
  }

  /**
   * Returns true when the error response matches the backend's empty-parts
   * duplicate guard message.
   */
  private static isEmptyPartsGuardError(response: ErrorResponse): boolean {
    // Matches EMPTY_MESSAGE_PARTS_CONVERSATION_STARTED in
    // send_message_endpoint_mixin.rb. The message-substring fallback covers
    // servers deployed before the error_code field existed.
    return (
      response.errorCode === 'empty_message_parts_conversation_started' ||
      (typeof response.error === 'string' &&
        response.error.includes('received empty message parts'))
    );
  }

  private emitDelegateFirstMessageToServerFailureMetric(
    tags: DelegateFirstMessageToServerFailureMetricTags,
  ): void {
    this.deps.emitDelegateFirstMessageToServerFailureMetric?.({
      failure_step: 'delegate_first_message_to_server',
      ...tags,
    });
  }

  private static shouldRetryApiError(response: SendMessageResult): boolean {
    return response.type === 'error' && response.code >= 500;
  }

  private static buildDelegatedFirstMessageParts(
    params: ValidatedStartConversationParams,
  ): ConversationsApiSendMessageEndpointRequest['parts'] {
    const initialQuery = params.context.triggerParams?.initialQuery;
    const initialAttachment = params.context.triggerParams?.initialAttachment;
    const parts: ConversationsApiSendMessageEndpointRequest['parts'] = [];
    let partId = 1;

    if (typeof initialQuery === 'string' && initialQuery.length > 0) {
      parts.push({id: partId, type: 'text', content: {text: initialQuery}});
      partId += 1;
    }

    if (initialAttachment) {
      parts.push({
        id: partId,
        type: 'attachment',
        content: {
          text: initialAttachment.name,
          name: initialAttachment.name,
          size: initialAttachment.size,
          attachment_type: initialAttachment.type,
          upload_token: initialAttachment.upload_token,
        },
      });
      partId += 1;
    }
    return parts;
  }

  private static validateStartConversationParams(
    params: StartConversationParams,
  ): ValidatedStartConversationParams | null {
    if (!params.participant) {
      return null;
    }

    if (!params.context?.audience) {
      return null;
    }

    if (!params.context?.entryPoint) {
      return null;
    }

    return params as ValidatedStartConversationParams;
  }

  private static requiredParamError(
    params: StartConversationParams,
  ): StartConversationResponse {
    if (!params.participant) {
      return StartConversationHandler.error(400, 'Missing user');
    }

    if (!params.context?.audience) {
      return StartConversationHandler.error(400, 'Missing audience');
    }

    return StartConversationHandler.error(400, 'Missing entrypoint');
  }

  private static buildStartPayload(
    params: ValidatedStartConversationParams,
  ): ConversationsApiCreateConversationEndpointRequest {
    const {context} = params;
    const urlSearchParams = StartConversationHandler.getUrlSearchParams();
    const forestVersion = urlSearchParams.get('forestVersion') || undefined;
    const additionalParams = StartConversationHandler.buildAdditionalParams(
      urlSearchParams,
      context,
    );
    const pageContext = StartConversationHandler.buildPageContext(params.data);

    return {
      audience: context.audience,
      entry_point: context.entryPoint,
      trigger_point: context.triggerParams
        ?.triggerPoint as ConversationsApiCreateConversationEndpointRequest['trigger_point'],
      forest_version: forestVersion,
      refcode: context.triggerParams?.refcode,
      additional_params:
        Object.keys(additionalParams).length > 0 ? additionalParams : undefined,
      page_context: pageContext,
    };
  }

  private static buildAdditionalParams(
    searchParams: URLSearchParams,
    context: ValidatedStartConversationParams['context'],
  ): Record<string, string> {
    // Code-supplied additionalParams are written first so they take
    // precedence over ambient URL params for the same key.
    const additionalParams: Record<string, string> = {
      ...(context.triggerParams?.additionalParams ?? {}),
    };

    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (
        !StartConversationHandler.AMBIENT_PARAMS_EXCLUDED_FROM_ADDITIONAL_PARAMS.has(
          key,
        ) &&
        !additionalParams[key]
      ) {
        additionalParams[key] = value;
      }
    });

    // Only include refcode if it is not already defined in the URL params or
    // triggerParams.additionalParams.
    if (context.triggerParams?.refcode && !additionalParams.refcode) {
      additionalParams.refcode = context.triggerParams.refcode;
    }

    if (
      context.assistantAccessibleViaSearch &&
      !additionalParams.assistant_accessible_via_search
    ) {
      additionalParams.assistant_accessible_via_search = String(
        context.assistantAccessibleViaSearch,
      );
    }

    return additionalParams;
  }

  private static buildPageContext(
    data: StartConversationParams['data'],
  ): ConversationsApiCreateConversationEndpointRequest['page_context'] {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return undefined;
    }

    const candidate = PageContextNormalizer.extractPageContextCandidate(data);
    if (!candidate) {
      return undefined;
    }

    const pageContext =
      PageContextNormalizer.normalizePageContextForCreate(candidate);
    return Object.keys(pageContext).length > 0
      ? (pageContext as ConversationsApiCreateConversationEndpointRequest['page_context'])
      : undefined;
  }

  private toStartConversationResponse(
    response: SendMessageResult,
    params: ValidatedStartConversationParams,
  ): StartConversationResponse {
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const frameworkConversation = FrameworkAdapter.fromApiConversation({
      apiConversation: response.data,
      context: params.context,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
      user: params.participant,
      isActive: true,
      reportWarning: this.deps.reportWarning,
    });

    const conversation = this.deps.buildConversationInstance({
      frameworkConversation,
      params,
    });

    return {code: 200, data: {conversation}};
  }

  private async findExistingActiveConversation(
    payload: ConversationsApiCreateConversationEndpointRequest,
  ): Promise<string | null> {
    const triggerPoint = payload.trigger_point;
    const response = await this.deps.listConversations({
      audience: payload.audience,
      entry_point: payload.entry_point,
      trigger_point:
        triggerPoint && isValidTriggerPoint(triggerPoint)
          ? triggerPoint
          : undefined,
      status: 'active',
      limit: 1,
    });

    if (response.type === 'error') {
      return null;
    }

    const [conversation] = response.data;
    if (!conversation?.id || !conversation.is_active) {
      return null;
    }

    return conversation.id;
  }

  private toRetrieveConversationResponse(
    response: GetConversationResult,
    params: RetrieveStartConversationParams,
  ): StartConversationResponse {
    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }

    const frameworkConversation = FrameworkAdapter.fromApiConversation({
      apiConversation: response.data,
      context: params.context,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
      user: params.participant,
      // This legacy path resumes the composite interaction. It can remain
      // active for live chat or callback after the bot overview is inactive.
      // Direct getConversation uses the overview's authoritative status.
      isActive: true,
      reportWarning: this.deps.reportWarning,
    });

    const conversation = this.deps.buildConversationInstance({
      frameworkConversation,
      params,
    });

    return {code: 200, data: {conversation}};
  }

  private static error(code: number, error: string): StartConversationResponse {
    return {
      code,
      error,
    };
  }

  private static getUrlSearchParams() {
    if (typeof window === 'undefined') {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }
}
